import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useActivePromo } from "@/lib/whatsapp-promo/hooks/use-active-promo";

/** Show the promo at most once every 3 days per user + event. */
const SHOW_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * localStorage key scoped to both the user and the promo event id.
 * Keying by event id means a brand-new campaign is not suppressed by an
 * older event's timer.
 */
export function promoLastShownKey(userId: string, promoId: string): string {
  return `whatsapp-promo-last-shown:${userId}:${promoId}`;
}

/**
 * Determines whether the WhatsApp promo popup should be shown.
 *
 * Shows when:
 * - User is authenticated
 * - Not on the login / root landing page
 * - An active promo event exists (with at least one image)
 * - The per-user + per-event 3-day cap has not been tripped
 */
export function useShouldShowWhatsAppPromo() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const { promo } = useActivePromo();

  const isOnLoginPage = location.pathname === "/login" || location.pathname === "/";

  const hasImages = (promo?.images.length ?? 0) > 0;

  if (!isAuthenticated || !user || isOnLoginPage || !promo || !hasImages) {
    return { shouldShow: false, promo: null } as const;
  }

  const stored = localStorage.getItem(promoLastShownKey(user.id, promo.id));
  const withinCooldown = stored !== null && Date.now() - Number(stored) <= SHOW_INTERVAL_MS;

  return { shouldShow: !withinCooldown, promo } as const;
}
